import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
// Native MUI Components
import { 
  Button, 
  TextField, 
  Container, 
  Paper, 
  Box, 
  Typography, 
  Avatar 
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useI18n();

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('pages.login.unknownError');
      toast({
        title: t('pages.login.loginError'),
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      navigate('/');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('pages.login.unknownError');
      toast({
        title: t('pages.login.googleLoginError'),
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        gap: 2 
      }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 400 }}>
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
            {t('pages.login.title')}
          </Typography>
          
          <Box component="form" onSubmit={handleEmailLogin} sx={{ mt: 2 }}>
            <TextField
              id="email"
              label={t('pages.login.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              margin="normal"
            />
            <TextField
              id="password"
              label={t('pages.login.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              margin="normal"
            />
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth 
              sx={{ mt: 3, mb: 2 }}
            >
              {t('pages.login.emailLoginButton')}
            </Button>
          </Box>
          
          <Button
            onClick={handleGoogleLogin}
            variant="outlined"
            fullWidth
            startIcon={<GoogleIcon />}
            sx={{ 
              color: 'text.primary',
              borderColor: 'grey.300',
              '&:hover': {
                bgcolor: 'grey.50',
                borderColor: 'grey.400'
              }
            }}
          >
            {t('pages.login.googleLoginButton')}
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
